import type { PatientData, UbaPatientData } from './types'
import type { ZodError } from 'zod'

import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router'
import { useNavigate } from 'react-router-dom'

import http from '../../lib/http'
import { LoadingIndicator } from '../claim-history/simple-table/loading-indicator'
import { useGlobalLoadingIndicator } from '../global-loading-indicator'
import { PlainButton } from '../shared/Button'
import * as Card from '../shared/card'

import { EditPatientForm } from './components/edit-user-form'
import { ErrorHandler } from './components/error-handler'
import { updatePatientSchema, useUbaUpdateMutation } from './queries'

const CREATE_UBA_USER_LOADER_ID = 'create-uba-user'

export type PatientState = PatientData & { draft: boolean }
export type ResponseUbaPatientState = UbaPatientData & { draft: boolean }

export function EditUserPage (): JSX.Element {
  let updateMutation = useUbaUpdateMutation()
  const navigate = useNavigate()
  const globalLoading = useGlobalLoadingIndicator(CREATE_UBA_USER_LOADER_ID)
  const params = useParams<{ id: string, clientName: string }>()
  const [ state, setState ] = useState<Partial<PatientState>>()
  const [ isValid, setIsValid ] = useState(false)
  const [ latestError, setLatestError ] = useState(null)
  const [ formError, setFormError ] = useState<ZodError | null>(null)

  async function fetchUser (id: string): Promise<PatientData> {
    let response = await http.getRequest<ResponseUbaPatientState>(`/uba/${id}/info`, {})
    const {
      addressLine1,
      addressLine2,
      patientName,
      patientGroup,
      cityName,
      patientId,
      dob,
      allergies,
      medicalCondition,
      patientNotes,
      phone,
      currentCondition,
      isBilling,
      email,
      zipCode,
      patientGUID,
      gender,
      stateCode
    } = response.data

    const [ firstName, lastName ] = patientName.split(' ')

    const newState: PatientState = {
      ubaUser: true,
      draft: false,
      data: {
        patientId,
        patientGroup,
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        addressLine1,
        addressLine2,
        cityName,
        dob: new Date(dob).toISOString().split('T')[0] ?? '',
        currentCondition,
        allergies,
        isBillingAddress: isBilling,
        medicalCondition,
        patientNotes,
        phone,
        email,
        zipCode,
        stateCode: stateCode ?? '',
        ubaPatientId: patientGUID,
        gender,
        countryCode: 'US'

      }
    }

    setState(newState)
    const { success } = updatePatientSchema.safeParse(newState.data)
    setIsValid(success)
    return newState
  }

  useEffect(() => {
    const { success, error } = updatePatientSchema.safeParse(state?.data)
    setIsValid(success)

    setFormError(error ?? null)
  }, [ state ])

  function handleSubmit (): void {
    const parsed = updatePatientSchema.safeParse(state?.data)

    if (parsed.success) {
      updateMutation.mutate({ data: parsed?.data, id: params.id ?? '' }, {
        async onSuccess () {
          navigate(`/patient/${params?.clientName ?? ''}/${params?.id ?? ''}/assessment/uba`)
        },
        async onError (e: any) {
          setLatestError(e?.response?.data ?? { message: e?.message })
        },
        async onSettled () {
          globalLoading.finishLoading()
        }
      })
    }
  }

  useEffect(() => {
    fetchUser(params.id ?? '')
      .catch(() => {
        toast.error("Something went wrong while fetching user's data")
      })
  }, [ params.id ])

  if (!state) {
    return (
      <LoadingIndicator loading />
    )
  }

  return (
    <Card.Card expandable>
      <Card.Header>
        <div className='w-full'>
          <h2>Edit Uba Care User</h2>
        </div>
      </Card.Header>
      <Card.ExpandableWrapper>
        <EditPatientForm
          state={state}
          setState={setState}
          formError={formError}
        />
      </Card.ExpandableWrapper>

      <div className='px-4'>
        {updateMutation.isError && <ErrorHandler error={latestError} />}
      </div>

      <hr className='my-[28px]' />
      <div
        style={{
          textAlign: 'right',
          padding: '0 28px 28px 0'
        }}
      >
        <PlainButton
          style={{ marginLeft: '6px' }}
          label='Cancel'
          btnType='contained'
          onClick={() => {
            navigate(`/patient/${params?.clientName ?? ''}/${params?.id ?? ''}/assessment/uba`)
          }}
        />
        <PlainButton
          style={{ marginLeft: '12px' }}
          disabled={!isValid || updateMutation.isPending}
          label='Publish'
          color='primary'
          btnType='contained'
          onClick={() => {
            globalLoading.startLoading('Creating Uba User')
            handleSubmit()
          }}
        />
      </div>
    </Card.Card>
  )
}
